import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Plus, Search, BookOpen, Briefcase, Users, Settings,
  Copy, RefreshCw, Archive, Loader2, ArrowLeft, Check, X
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORES = [
  { value: '#7c3aed', label: 'Violeta' },
  { value: '#059669', label: 'Verde' },
  { value: '#2563eb', label: 'Azul' },
  { value: '#dc2626', label: 'Rojo' },
  { value: '#d97706', label: 'Naranja' },
];

export default function CampusAdminPage() {
  const [espacios, setEspacios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '', descripcion: '', tipo: 'curso',
    color_tema: '#7c3aed', modalidad: 'virtual'
  });
  const [saving, setSaving] = useState(false);
  const [copiado, setCopiado] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => { fetchEspacios(); }, []);

  const fetchEspacios = async () => {
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/todos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEspacios(res.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const crearEspacio = async () => {
    if (!formData.nombre.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/campus/espacios`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowForm(false);
      setFormData({ nombre: '', descripcion: '', tipo: 'curso', color_tema: '#7c3aed', modalidad: 'virtual' });
      fetchEspacios();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear espacio');
    } finally {
      setSaving(false);
    }
  };

  const copiarCodigo = (codigo) => {
    navigator.clipboard.writeText(codigo);
    setCopiado(codigo);
    setTimeout(() => setCopiado(''), 2000);
  };

  const archivarEspacio = async (spaceId) => {
    if (!window.confirm('¿Archivar este espacio?')) return;
    try {
      await axios.put(`${API_URL}/campus/espacios/${spaceId}`,
        { estado: 'archivado' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchEspacios();
    } catch (err) {
      alert('Error al archivar');
    }
  };

  const filtrados = espacios.filter(e =>
    e.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const cursos = filtrados.filter(e => e.tipo === 'curso');
  const comisiones = filtrados.filter(e => e.tipo === 'comision');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="campus-admin-title">
            Campus Virtual - Administración
          </h1>
          <p className="text-sm text-gray-500">
            Gestionar cursos y comisiones del Campus
          </p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} data-testid="create-space-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Espacio
        </Button>
      </div>

      {/* Formulario de creación */}
      {showForm && (
        <Card data-testid="create-space-form">
          <CardHeader>
            <CardTitle className="text-lg">Crear nuevo espacio</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Nombre *</label>
                <Input
                  placeholder="Ej: Curso de Mediación Familiar"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  data-testid="space-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Tipo</label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  data-testid="space-type-select"
                >
                  <option value="curso">Curso</option>
                  <option value="comision">Comisión</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Descripción</label>
              <textarea
                className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px]"
                placeholder="Descripción del espacio..."
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                data-testid="space-desc-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Modalidad</label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  value={formData.modalidad}
                  onChange={(e) => setFormData({ ...formData, modalidad: e.target.value })}
                  data-testid="space-modality-select"
                >
                  <option value="virtual">Virtual</option>
                  <option value="presencial">Presencial</option>
                  <option value="mixta">Mixta</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Color</label>
                <div className="flex gap-2 mt-1">
                  {COLORES.map(c => (
                    <button
                      key={c.value}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${formData.color_tema === c.value ? 'border-gray-900 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c.value }}
                      onClick={() => setFormData({ ...formData, color_tema: c.value })}
                      title={c.label}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button onClick={crearEspacio} disabled={saving || !formData.nombre.trim()} data-testid="save-space-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Crear Espacio
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Búsqueda */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Buscar espacios..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="admin-search-spaces"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-violet-600">{espacios.length}</div>
            <div className="text-xs text-gray-500">Total Espacios</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{cursos.length}</div>
            <div className="text-xs text-gray-500">Cursos</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-emerald-600">{comisiones.length}</div>
            <div className="text-xs text-gray-500">Comisiones</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <div className="text-2xl font-bold text-gray-600">
              {espacios.reduce((sum, e) => sum + (e.total_miembros || 0), 0)}
            </div>
            <div className="text-xs text-gray-500">Total Miembros</div>
          </CardContent>
        </Card>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
        </div>
      )}

      {/* Lista de espacios */}
      {!loading && filtrados.length === 0 && (
        <Card className="text-center py-8">
          <CardContent>
            <p className="text-gray-500">No hay espacios creados todavía.</p>
          </CardContent>
        </Card>
      )}

      {!loading && filtrados.map(espacio => (
        <Card key={espacio.id} className="hover:shadow-md transition-shadow" data-testid={`admin-space-card-${espacio.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: espacio.color_tema || '#7c3aed' }}
              >
                {espacio.tipo === 'curso'
                  ? <BookOpen className="w-6 h-6 text-white" />
                  : <Briefcase className="w-6 h-6 text-white" />
                }
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-gray-900">{espacio.nombre}</h3>
                  <Badge variant="outline" className="text-xs">
                    {espacio.tipo === 'curso' ? 'Curso' : 'Comisión'}
                  </Badge>
                  <Badge variant="secondary" className="text-xs capitalize">
                    {espacio.modalidad}
                  </Badge>
                  {espacio.estado === 'archivado' && (
                    <Badge variant="destructive" className="text-xs">Archivado</Badge>
                  )}
                </div>
                {espacio.descripcion && (
                  <p className="text-sm text-gray-500 mt-1 line-clamp-1">{espacio.descripcion}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {espacio.total_miembros || 0} miembros
                  </span>
                  <span className="flex items-center gap-1 font-mono">
                    Código: <strong>{espacio.codigo_acceso}</strong>
                    <button
                      onClick={() => copiarCodigo(espacio.codigo_acceso)}
                      className="hover:text-violet-600"
                      title="Copiar código"
                    >
                      {copiado === espacio.codigo_acceso
                        ? <Check className="w-3.5 h-3.5 text-green-600" />
                        : <Copy className="w-3.5 h-3.5" />
                      }
                    </button>
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/campus/espacio/${espacio.id}`)}
                  data-testid={`view-space-${espacio.id}`}
                >
                  Ver
                </Button>
                {espacio.estado !== 'archivado' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => archivarEspacio(espacio.id)}
                    title="Archivar"
                  >
                    <Archive className="w-4 h-4 text-gray-400" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
